<script>
  import { navigate } from "svelte-routing";
  import api from "../../services/api.js";
  import { auth } from "../../stores/auth.js";

  let step = 0; // Start at step 0 for eligibility check
  const totalSteps = 6; // Added eligibility step
  let submitting = false;
  let error = null;
  let fieldErrors = {};

  // Eligibility check
  let eligibilityAnswers = {
    isUSResident: null, // null = not answered, true/false = answer
    isOver18: null,
    hasSSN: null,
  };
  let eligibilityRejected = false;

  // US States
  const usStates = [
    { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "Washington D.C." }
  ];

  // Asset ranges for liquid/investible assets
  const assetRanges = [
    { value: "0-20000", label: "$0 - $20,000" },
    { value: "20000-50000", label: "$20,000 - $50,000" },
    { value: "50000-100000", label: "$50,000 - $100,000" },
    { value: "100000-500000", label: "$100,000 - $500,000" },
    { value: "500000-1000000", label: "$500,000 - $1,000,000" },
    { value: "1000000-10000000", label: "$1,000,000+" },
  ];

  // Income ranges (optional)
  const incomeRanges = [
    { value: "", label: "Prefer not to say" },
    { value: "0-25000", label: "$0 - $25,000" },
    { value: "25000-50000", label: "$25,000 - $50,000" },
    { value: "50000-100000", label: "$50,000 - $100,000" },
    { value: "100000-200000", label: "$100,000 - $200,000" },
    { value: "200000-500000", label: "$200,000 - $500,000" },
    { value: "500000+", label: "$500,000+" },
  ];

  // Funding sources
  const fundingSources = [
    { value: "employment_income", label: "Employment Income" },
    { value: "investments", label: "Investments" },
    { value: "business_income", label: "Business Income" },
    { value: "savings", label: "Savings" },
    { value: "inheritance", label: "Inheritance" },
    { value: "family", label: "Family" },
  ];

  // Employment status
  const employmentStatuses = [
    { value: "employed", label: "Employed" },
    { value: "self_employed", label: "Self-Employed" },
    { value: "unemployed", label: "Unemployed" },
    { value: "student", label: "Student" },
    { value: "retired", label: "Retired" },
  ];

  // Disclosure PDFs from Alpaca
  const disclosures = {
    customerAgreement: "https://files.alpaca.markets/disclosures/library/AcctAppMarginAndCustAgmt.pdf",
    useAndRisk: "https://files.alpaca.markets/disclosures/library/UseAndRisk.pdf",
    termsAndConditions: "https://files.alpaca.markets/disclosures/library/TermsAndConditions.pdf",
    privacyNotice: "https://files.alpaca.markets/disclosures/library/PrivacyNotice.pdf",
    pfof: "https://files.alpaca.markets/disclosures/library/PFOF.pdf",
    marginDisclosure: "https://files.alpaca.markets/disclosures/library/MarginDiscStmt.pdf",
    extendedHours: "https://files.alpaca.markets/disclosures/library/ExtHrsRisk.pdf",
    bcp: "https://files.alpaca.markets/disclosures/library/BCPSummary.pdf",
    formCRS: "https://files.alpaca.markets/disclosures/library/FormCRS.pdf",
    disclosuresPage: "https://alpaca.markets/disclosures",
  };

  let formData = {
    // Personal Information
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: "",
    tax_id: "",
    phone_number: "",

    // Address - USA only
    street_address: "",
    unit_apt: "",
    city: "",
    state: "",
    postal_code: "",
    country: "USA",

    // Financial Profile
    liquid_assets: "",
    annual_income: "",
    funding_source: "employment_income",
    employment_status: "employed",

    // Employment details (required to ask if employed)
    employer_name: "",
    employer_address: "",
    job_title: "",

    // Affiliations (must ask, cannot be hardcoded)
    is_control_person: false,
    is_affiliated_exchange_or_finra: false,
    is_politically_exposed: false,
    immediate_family_exposed: false,

    // Affiliate details (if any affiliation is true)
    affiliated_company_name: "",
    affiliated_company_address: "",
    affiliated_company_ticker: "",

    // Agreements
    customer_agreement_accepted: false,
    electronic_signature_accepted: false,
  };

  // Validation functions
  function validateName(name, field = "Name") {
    if (!name || name.trim().length < 1) return `${field} is required`;
    if (name.length > 50) return `${field} is too long`;
    if (!/^[a-zA-Z\s\-']+$/.test(name)) return "Only letters, spaces, hyphens, and apostrophes allowed";
    return null;
  }

  function validateDateOfBirth(dob) {
    if (!dob) return "Date of birth is required";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 18) return "You must be at least 18 years old";
    if (age > 120) return "Please enter a valid date of birth";
    return null;
  }

  function validateSSN(ssn) {
    if (!ssn) return "SSN is required";
    const cleanSSN = ssn.replace(/\D/g, '');
    if (cleanSSN.length !== 9) return "SSN must be exactly 9 digits";
    if (/^0{3}/.test(cleanSSN)) return "SSN cannot start with 000";
    if (/^\d{3}0{2}/.test(cleanSSN)) return "Middle two digits cannot be 00";
    if (/^\d{5}0{4}$/.test(cleanSSN)) return "Last four digits cannot be 0000";
    return null;
  }

  function formatSSN(value) {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
  }

  function handleSSNInput(event) {
    formData.tax_id = formatSSN(event.target.value);
  }

  function validatePhone(phone) {
    if (!phone) return "Phone number is required";
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) return "Phone number must be at least 10 digits";
    if (cleaned.length > 15) return "Phone number is too long";
    return null;
  }

  function formatPhone(value) {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  function handlePhoneInput(event) {
    formData.phone_number = formatPhone(event.target.value);
  }

  function validateAddress(address) {
    if (!address || address.trim().length < 5) return "Please enter a valid street address";
    // Check for PO Box
    if (/p\.?\s*o\.?\s*box/i.test(address)) return "PO Boxes are not allowed. Please use a physical address.";
    return null;
  }

  function validateCity(city) {
    if (!city || city.trim().length < 2) return "Please enter a valid city";
    if (!/^[a-zA-Z\s\-'.]+$/.test(city)) return "City contains invalid characters";
    return null;
  }

  function validateState(state) {
    if (!state) return "Please select a state";
    return null;
  }

  function validatePostalCode(code) {
    if (!code) return "ZIP code is required";
    if (!/^\d{5}(-\d{4})?$/.test(code)) return "Enter valid ZIP code (12345 or 12345-6789)";
    return null;
  }

  function validateLiquidAssets(assets) {
    if (!assets) return "Liquid/investible assets is required";
    return null;
  }

  function validateStep1() {
    fieldErrors = {};

    const firstNameError = validateName(formData.first_name, "First name");
    if (firstNameError) fieldErrors.first_name = firstNameError;

    const lastNameError = validateName(formData.last_name, "Last name");
    if (lastNameError) fieldErrors.last_name = lastNameError;

    // Middle name is optional, but if provided, validate it
    if (formData.middle_name && formData.middle_name.trim()) {
      const middleNameError = validateName(formData.middle_name, "Middle name");
      if (middleNameError) fieldErrors.middle_name = middleNameError;
    }

    const dobError = validateDateOfBirth(formData.date_of_birth);
    if (dobError) fieldErrors.date_of_birth = dobError;

    const ssnError = validateSSN(formData.tax_id);
    if (ssnError) fieldErrors.tax_id = ssnError;

    const phoneError = validatePhone(formData.phone_number);
    if (phoneError) fieldErrors.phone_number = phoneError;

    return Object.keys(fieldErrors).length === 0;
  }

  function validateStep2() {
    fieldErrors = {};

    const addressError = validateAddress(formData.street_address);
    if (addressError) fieldErrors.street_address = addressError;

    const cityError = validateCity(formData.city);
    if (cityError) fieldErrors.city = cityError;

    const stateError = validateState(formData.state);
    if (stateError) fieldErrors.state = stateError;

    const postalError = validatePostalCode(formData.postal_code);
    if (postalError) fieldErrors.postal_code = postalError;

    return Object.keys(fieldErrors).length === 0;
  }

  function validateStep3() {
    fieldErrors = {};

    const assetsError = validateLiquidAssets(formData.liquid_assets);
    if (assetsError) fieldErrors.liquid_assets = assetsError;

    // Employment details: ask but allow skip (Alpaca requires monthly re-prompt if skipped)
    // We'll validate funding_source and employment_status are selected
    if (!formData.funding_source) fieldErrors.funding_source = "Please select a funding source";
    if (!formData.employment_status) fieldErrors.employment_status = "Please select employment status";

    return Object.keys(fieldErrors).length === 0;
  }

  function validateStep4() {
    // Affiliations step - questions must be answered (even if all false)
    fieldErrors = {};
    // No validation needed - checkboxes default to false
    return true;
  }

  function validateStep5() {
    fieldErrors = {};

    if (!formData.customer_agreement_accepted) {
      fieldErrors.customer_agreement = "You must read and agree to the Customer Agreement";
    }
    if (!formData.electronic_signature_accepted) {
      fieldErrors.electronic_signature = "You must agree to sign electronically";
    }

    return Object.keys(fieldErrors).length === 0;
  }

  function validateStep0() {
    // Check if all eligibility questions are answered
    if (eligibilityAnswers.isUSResident === null) {
      error = "Please answer all eligibility questions";
      return false;
    }
    if (eligibilityAnswers.isOver18 === null) {
      error = "Please answer all eligibility questions";
      return false;
    }
    if (eligibilityAnswers.hasSSN === null) {
      error = "Please answer all eligibility questions";
      return false;
    }

    // Check if user is eligible
    if (!eligibilityAnswers.isUSResident || !eligibilityAnswers.isOver18 || !eligibilityAnswers.hasSSN) {
      eligibilityRejected = true;
      return false;
    }

    return true;
  }

  function nextStep() {
    error = null;

    let valid = false;
    switch (step) {
      case 0: valid = validateStep0(); break;
      case 1: valid = validateStep1(); break;
      case 2: valid = validateStep2(); break;
      case 3: valid = validateStep3(); break;
      case 4: valid = validateStep4(); break;
      case 5: valid = validateStep5(); break;
    }

    if (!valid) {
      if (!eligibilityRejected) {
        error = error || "Please fix the errors below";
      }
      return;
    }

    if (step < totalSteps) step++;
  }

  function prevStep() {
    fieldErrors = {};
    error = null;
    if (step > 0) step--;
  }

  async function handleSubmit() {
    if (!validateStep5()) {
      error = "Please accept the required agreements";
      return;
    }

    // Final validation of all steps
    if (!validateStep1() || !validateStep2() || !validateStep3()) {
      error = "Please go back and fix all errors before submitting";
      return;
    }

    submitting = true;
    error = null;

    try {
      const cleanPhone = formData.phone_number.replace(/\D/g, '');
      const submitData = {
        // Personal Info
        first_name: formData.first_name.trim(),
        middle_name: formData.middle_name?.trim() || null,
        last_name: formData.last_name.trim(),
        date_of_birth: formData.date_of_birth,
        tax_id: formData.tax_id.replace(/\D/g, ''),
        phone_number: "+1" + cleanPhone, // US only, always +1

        // Address
        street_address: formData.street_address.trim(),
        unit_apt: formData.unit_apt?.trim() || null,
        city: formData.city.trim(),
        state: formData.state,
        postal_code: formData.postal_code,
        country: "USA",

        // Financial - all US citizens
        country_of_tax_residence: "USA",
        country_of_citizenship: "USA",
        country_of_birth: "USA",

        // Financial Profile
        liquid_assets: formData.liquid_assets,
        annual_income: formData.annual_income || null,
        funding_source: formData.funding_source,
        employment_status: formData.employment_status,

        // Employment details (optional)
        employer_name: formData.employer_name?.trim() || null,
        employer_address: formData.employer_address?.trim() || null,
        job_title: formData.job_title?.trim() || null,

        // Affiliations
        is_control_person: formData.is_control_person,
        is_affiliated_exchange_or_finra: formData.is_affiliated_exchange_or_finra,
        is_politically_exposed: formData.is_politically_exposed,
        immediate_family_exposed: formData.immediate_family_exposed,

        // Affiliate company details
        affiliated_company_name: formData.affiliated_company_name?.trim() || null,
        affiliated_company_address: formData.affiliated_company_address?.trim() || null,
        affiliated_company_ticker: formData.affiliated_company_ticker?.trim() || null,

        // Agreements
        customer_agreement_accepted: formData.customer_agreement_accepted,
        electronic_signature_accepted: formData.electronic_signature_accepted,
      };

      await api.submitOnboarding(submitData);
      await auth.init();
      navigate("/admin/dashboard");
    } catch (e) {
      error = e.message;
    }
    submitting = false;
  }

  // Calculate max date for date of birth (18 years ago)
  function getMaxDOB() {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split('T')[0];
  }

  function getMinDOB() {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 120);
    return date.toISOString().split('T')[0];
  }

  // Check if user needs to provide affiliate details
  $: needsAffiliateDetails = formData.is_control_person || formData.is_affiliated_exchange_or_finra;

  // Check if employment details should be shown
  $: showEmploymentDetails = formData.employment_status === "employed" || formData.employment_status === "self_employed";

  // Presentational: stepper labels (order matches step indices)
  const stepLabels = ["Eligibility", "Personal", "Address", "Financial", "Disclosures", "Agreements"];
</script>

<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Account setup</h1>
  <p class="text-sm text-ink-secondary mt-1">Open your Moira Finance brokerage account in a few measured steps.</p>
</header>

<div class="max-w-2xl mx-auto">
  {#if error}
    <div class="banner banner-danger mb-6 flex items-start justify-between gap-4">
      <span>{error}</span>
      <button on:click={() => error = null} class="font-bold leading-none shrink-0" aria-label="Dismiss">&times;</button>
    </div>
  {/if}

  <!-- Eligibility Rejection Screen -->
  {#if eligibilityRejected}
    <div class="card card-pad text-center mb-6">
      <div class="w-11 h-11 rounded-xl bg-red-50 inline-flex items-center justify-center mb-4">
        <i class="fas fa-times text-danger text-lg"></i>
      </div>
      <h2 class="text-xl font-semibold text-ink mb-2">
        We're sorry, you're not eligible
      </h2>
      <p class="text-sm text-ink-secondary mb-6 max-w-lg mx-auto">
        Unfortunately, Moira Finance is currently only available to:
      </p>
      <ul class="text-left max-w-md mx-auto mb-6 space-y-2">
        <li class="flex items-center text-sm text-ink">
          <i class="fas fa-check-circle text-moira mr-2"></i>
          United States residents
        </li>
        <li class="flex items-center text-sm text-ink">
          <i class="fas fa-check-circle text-moira mr-2"></i>
          Individuals 18 years of age or older
        </li>
        <li class="flex items-center text-sm text-ink">
          <i class="fas fa-check-circle text-moira mr-2"></i>
          Those with a valid Social Security Number
        </li>
      </ul>
      <p class="text-xs text-ink-tertiary mb-6">
        These requirements are mandated by our brokerage partner, Alpaca Securities LLC,
        and are required for all U.S. brokerage accounts.
      </p>
      <div class="flex justify-center gap-3">
        <button
          on:click={() => { eligibilityRejected = false; step = 0; }}
          class="btn btn-outline"
        >
          <i class="fas fa-arrow-left text-ink-tertiary"></i>
          Go back
        </button>
        <button
          on:click={() => navigate('/')}
          class="btn btn-primary"
        >
          Return home
        </button>
      </div>
    </div>
  {:else}
    <div class="card mb-6">
      <!-- Stepper -->
      <div class="p-6 border-b border-line">
        <div class="flex items-center justify-between mb-5">
          <h2 class="text-lg font-semibold text-ink">Complete your account setup</h2>
          <span class="text-sm text-ink-tertiary tnum">Step {step + 1} of {totalSteps}</span>
        </div>
        <div class="flex items-start">
          {#each stepLabels as label, i}
            {#if i > 0}
              <div class="flex-1 h-px bg-line mt-3.5 mx-2"></div>
            {/if}
            <div class="flex flex-col items-center gap-1.5">
              {#if step > i}
                <div class="w-7 h-7 rounded-full bg-moira text-white flex items-center justify-center text-xs">
                  <i class="fas fa-check"></i>
                </div>
              {:else if step === i}
                <div class="w-7 h-7 rounded-full border-2 border-moira text-moira flex items-center justify-center text-xs font-semibold tnum">
                  {i + 1}
                </div>
              {:else}
                <div class="w-7 h-7 rounded-full border border-line text-ink-tertiary flex items-center justify-center text-xs font-semibold tnum">
                  {i + 1}
                </div>
              {/if}
              <span class="hidden sm:block text-xs {step === i ? 'text-ink font-medium' : 'text-ink-tertiary'}">{label}</span>
            </div>
          {/each}
        </div>
      </div>

      <div class="card-pad">
        <!-- Step 0: Eligibility Check -->
        {#if step === 0}
          <h3 class="text-lg font-semibold text-ink mb-1">Eligibility check</h3>
          <p class="text-sm text-ink-secondary mb-6">
            Before we begin, please confirm your eligibility to open an investment account with Moira Finance.
          </p>

          <div class="space-y-4">
            <!-- US Residency Question -->
            <div class="rounded-xl border border-line p-5">
              <p class="text-sm font-semibold text-ink mb-1">
                Are you a legal resident of the United States?
              </p>
              <p class="text-xs text-ink-tertiary mb-4">
                This includes U.S. citizens, permanent residents (green card holders), and those with valid U.S. residency visas.
              </p>
              <div class="flex gap-3">
                <button
                  type="button"
                  on:click={() => eligibilityAnswers.isUSResident = true}
                  class="btn flex-1 {eligibilityAnswers.isUSResident === true ? 'btn-primary' : 'btn-outline'}"
                >
                  <i class="fas fa-check"></i> Yes
                </button>
                <button
                  type="button"
                  on:click={() => eligibilityAnswers.isUSResident = false}
                  class="btn flex-1 {eligibilityAnswers.isUSResident === false ? 'btn-danger' : 'btn-outline'}"
                >
                  <i class="fas fa-times"></i> No
                </button>
              </div>
            </div>

            <!-- Age Question -->
            <div class="rounded-xl border border-line p-5">
              <p class="text-sm font-semibold text-ink mb-1">
                Are you 18 years of age or older?
              </p>
              <p class="text-xs text-ink-tertiary mb-4">
                You must be at least 18 years old to open an investment account.
              </p>
              <div class="flex gap-3">
                <button
                  type="button"
                  on:click={() => eligibilityAnswers.isOver18 = true}
                  class="btn flex-1 {eligibilityAnswers.isOver18 === true ? 'btn-primary' : 'btn-outline'}"
                >
                  <i class="fas fa-check"></i> Yes
                </button>
                <button
                  type="button"
                  on:click={() => eligibilityAnswers.isOver18 = false}
                  class="btn flex-1 {eligibilityAnswers.isOver18 === false ? 'btn-danger' : 'btn-outline'}"
                >
                  <i class="fas fa-times"></i> No
                </button>
              </div>
            </div>

            <!-- SSN Question -->
            <div class="rounded-xl border border-line p-5">
              <p class="text-sm font-semibold text-ink mb-1">
                Do you have a valid Social Security Number (SSN)?
              </p>
              <p class="text-xs text-ink-tertiary mb-4">
                A valid SSN is required for tax reporting purposes as mandated by the IRS.
              </p>
              <div class="flex gap-3">
                <button
                  type="button"
                  on:click={() => eligibilityAnswers.hasSSN = true}
                  class="btn flex-1 {eligibilityAnswers.hasSSN === true ? 'btn-primary' : 'btn-outline'}"
                >
                  <i class="fas fa-check"></i> Yes
                </button>
                <button
                  type="button"
                  on:click={() => eligibilityAnswers.hasSSN = false}
                  class="btn flex-1 {eligibilityAnswers.hasSSN === false ? 'btn-danger' : 'btn-outline'}"
                >
                  <i class="fas fa-times"></i> No
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-end mt-6">
            <button
              type="button"
              on:click={nextStep}
              disabled={eligibilityAnswers.isUSResident === null || eligibilityAnswers.isOver18 === null || eligibilityAnswers.hasSSN === null}
              class="btn btn-primary"
            >
              Continue <i class="fas fa-arrow-right"></i>
            </button>
          </div>

        <!-- Step 1: Personal Information -->
        {:else if step === 1}
          <h3 class="text-lg font-semibold text-ink mb-1">Personal information</h3>
          <p class="text-sm text-ink-secondary mb-6">
            Please enter your legal name exactly as it appears on your government-issued ID.
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <div>
              <label class="label" for="onb-first-name">
                Legal first name <span class="text-danger">*</span>
              </label>
              <input
                id="onb-first-name"
                type="text"
                bind:value={formData.first_name}
                placeholder="John"
                class="input {fieldErrors.first_name ? 'input-error' : ''}"
              />
              {#if fieldErrors.first_name}
                <p class="help-error">{fieldErrors.first_name}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-middle-name">
                Legal middle name <span class="text-ink-tertiary font-normal">(optional)</span>
              </label>
              <input
                id="onb-middle-name"
                type="text"
                bind:value={formData.middle_name}
                placeholder="Michael"
                class="input {fieldErrors.middle_name ? 'input-error' : ''}"
              />
              {#if fieldErrors.middle_name}
                <p class="help-error">{fieldErrors.middle_name}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-last-name">
                Legal last name <span class="text-danger">*</span>
              </label>
              <input
                id="onb-last-name"
                type="text"
                bind:value={formData.last_name}
                placeholder="Doe"
                class="input {fieldErrors.last_name ? 'input-error' : ''}"
              />
              {#if fieldErrors.last_name}
                <p class="help-error">{fieldErrors.last_name}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-dob">
                Date of birth <span class="text-danger">*</span>
              </label>
              <input
                id="onb-dob"
                type="date"
                bind:value={formData.date_of_birth}
                max={getMaxDOB()}
                min={getMinDOB()}
                class="input tnum {fieldErrors.date_of_birth ? 'input-error' : ''}"
              />
              <p class="text-xs text-ink-tertiary mt-1.5">You must be at least 18 years old</p>
              {#if fieldErrors.date_of_birth}
                <p class="help-error">{fieldErrors.date_of_birth}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-ssn">
                Social Security Number <span class="text-danger">*</span>
              </label>
              <input
                id="onb-ssn"
                type="text"
                value={formData.tax_id}
                on:input={handleSSNInput}
                placeholder="123-45-6789"
                maxlength="11"
                class="input tnum {fieldErrors.tax_id ? 'input-error' : ''}"
              />
              <p class="text-xs text-ink-tertiary mt-1.5">Required for tax reporting purposes</p>
              {#if fieldErrors.tax_id}
                <p class="help-error">{fieldErrors.tax_id}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-phone">
                Phone number <span class="text-danger">*</span>
              </label>
              <div class="flex">
                <span class="inline-flex items-center rounded-l-xl bg-surface-variant border-r border-line px-4 text-sm text-ink-secondary">
                  +1
                </span>
                <input
                  id="onb-phone"
                  type="tel"
                  value={formData.phone_number}
                  on:input={handlePhoneInput}
                  placeholder="(555) 123-4567"
                  class="input tnum rounded-l-none {fieldErrors.phone_number ? 'input-error' : ''}"
                />
              </div>
              {#if fieldErrors.phone_number}
                <p class="help-error">{fieldErrors.phone_number}</p>
              {/if}
            </div>
          </div>

        <!-- Step 2: Address Information -->
        {:else if step === 2}
          <h3 class="text-lg font-semibold text-ink mb-1">Permanent residential address</h3>
          <p class="text-sm text-ink-secondary mb-6">
            Please enter your permanent home address. PO Boxes are not accepted.
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <div class="sm:col-span-2">
              <label class="label" for="onb-street">
                Street address <span class="text-danger">*</span>
              </label>
              <input
                id="onb-street"
                type="text"
                bind:value={formData.street_address}
                placeholder="123 Main Street"
                class="input {fieldErrors.street_address ? 'input-error' : ''}"
              />
              {#if fieldErrors.street_address}
                <p class="help-error">{fieldErrors.street_address}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-unit">
                Unit / apt <span class="text-ink-tertiary font-normal">(optional)</span>
              </label>
              <input
                id="onb-unit"
                type="text"
                bind:value={formData.unit_apt}
                placeholder="Apt 4B"
                class="input"
              />
            </div>
            <div>
              <label class="label" for="onb-city">
                City <span class="text-danger">*</span>
              </label>
              <input
                id="onb-city"
                type="text"
                bind:value={formData.city}
                placeholder="Los Angeles"
                class="input {fieldErrors.city ? 'input-error' : ''}"
              />
              {#if fieldErrors.city}
                <p class="help-error">{fieldErrors.city}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-state">
                State <span class="text-danger">*</span>
              </label>
              <select
                id="onb-state"
                bind:value={formData.state}
                class="select {fieldErrors.state ? 'input-error' : ''}"
              >
                <option value="">Select state</option>
                {#each usStates as state}
                  <option value={state.code}>{state.name}</option>
                {/each}
              </select>
              {#if fieldErrors.state}
                <p class="help-error">{fieldErrors.state}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-zip">
                ZIP code <span class="text-danger">*</span>
              </label>
              <input
                id="onb-zip"
                type="text"
                bind:value={formData.postal_code}
                placeholder="90210"
                maxlength="10"
                class="input tnum {fieldErrors.postal_code ? 'input-error' : ''}"
              />
              {#if fieldErrors.postal_code}
                <p class="help-error">{fieldErrors.postal_code}</p>
              {/if}
            </div>
          </div>

        <!-- Step 3: Financial Profile -->
        {:else if step === 3}
          <h3 class="text-lg font-semibold text-ink mb-1">Financial profile</h3>
          <p class="text-sm text-ink-secondary mb-6">
            This information helps us ensure our services are appropriate for you.
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <div>
              <label class="label" for="onb-assets">
                Liquid / investible assets <span class="text-danger">*</span>
              </label>
              <select
                id="onb-assets"
                bind:value={formData.liquid_assets}
                class="select tnum {fieldErrors.liquid_assets ? 'input-error' : ''}"
              >
                <option value="">Select range</option>
                {#each assetRanges as range}
                  <option value={range.value}>{range.label}</option>
                {/each}
              </select>
              <p class="text-xs text-ink-tertiary mt-1.5">Cash and investments you could access within 30 days</p>
              {#if fieldErrors.liquid_assets}
                <p class="help-error">{fieldErrors.liquid_assets}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-income">
                Annual household income <span class="text-ink-tertiary font-normal">(optional)</span>
              </label>
              <select
                id="onb-income"
                bind:value={formData.annual_income}
                class="select tnum"
              >
                {#each incomeRanges as range}
                  <option value={range.value}>{range.label}</option>
                {/each}
              </select>
            </div>
            <div>
              <label class="label" for="onb-funding">
                Primary funding source <span class="text-danger">*</span>
              </label>
              <select
                id="onb-funding"
                bind:value={formData.funding_source}
                class="select {fieldErrors.funding_source ? 'input-error' : ''}"
              >
                {#each fundingSources as source}
                  <option value={source.value}>{source.label}</option>
                {/each}
              </select>
              {#if fieldErrors.funding_source}
                <p class="help-error">{fieldErrors.funding_source}</p>
              {/if}
            </div>
            <div>
              <label class="label" for="onb-employment">
                Employment status <span class="text-danger">*</span>
              </label>
              <select
                id="onb-employment"
                bind:value={formData.employment_status}
                class="select {fieldErrors.employment_status ? 'input-error' : ''}"
              >
                {#each employmentStatuses as status}
                  <option value={status.value}>{status.label}</option>
                {/each}
              </select>
              {#if fieldErrors.employment_status}
                <p class="help-error">{fieldErrors.employment_status}</p>
              {/if}
            </div>

            {#if showEmploymentDetails}
              <div class="sm:col-span-2">
                <div class="rounded-xl border border-line p-5">
                  <p class="text-sm font-semibold text-ink mb-1">
                    Employment details <span class="text-ink-tertiary font-normal">(optional)</span>
                  </p>
                  <p class="text-xs text-ink-tertiary mb-4">
                    These fields are optional, but regulatory requirements may require us to request this information again if not provided.
                  </p>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="label" for="onb-employer-name">Employer name</label>
                      <input
                        id="onb-employer-name"
                        type="text"
                        bind:value={formData.employer_name}
                        placeholder="Acme Corporation"
                        class="input"
                      />
                    </div>
                    <div>
                      <label class="label" for="onb-employer-address">Employer address</label>
                      <input
                        id="onb-employer-address"
                        type="text"
                        bind:value={formData.employer_address}
                        placeholder="123 Business Ave, City, ST"
                        class="input"
                      />
                    </div>
                    <div>
                      <label class="label" for="onb-job-title">Job title / occupation</label>
                      <input
                        id="onb-job-title"
                        type="text"
                        bind:value={formData.job_title}
                        placeholder="Software Engineer"
                        class="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            {/if}
          </div>

        <!-- Step 4: Affiliations & Disclosures -->
        {:else if step === 4}
          <h3 class="text-lg font-semibold text-ink mb-1">Regulatory disclosures</h3>
          <p class="text-sm text-ink-secondary mb-6">
            Federal regulations require us to ask the following questions. Please answer truthfully.
          </p>

          <div class="space-y-4">
            <div class="rounded-xl border border-line p-4">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={formData.is_control_person}
                  class="checkbox mt-1 mr-3"
                />
                <div>
                  <span class="text-sm font-medium text-ink">
                    Are you a control person of a publicly traded company?
                  </span>
                  <p class="text-xs text-ink-tertiary mt-1">
                    A control person is a director, 10% (or more) shareholder, policy-making officer, or other person who exercises a controlling influence over a public company.
                  </p>
                </div>
              </label>
            </div>

            <div class="rounded-xl border border-line p-4">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={formData.is_affiliated_exchange_or_finra}
                  class="checkbox mt-1 mr-3"
                />
                <div>
                  <span class="text-sm font-medium text-ink">
                    Are you or an immediate family member affiliated with a U.S. registered broker-dealer or FINRA?
                  </span>
                  <p class="text-xs text-ink-tertiary mt-1">
                    This includes being employed by, or having a direct relationship with, any broker-dealer, securities exchange, or regulatory organization.
                  </p>
                </div>
              </label>
            </div>

            <div class="rounded-xl border border-line p-4">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={formData.is_politically_exposed}
                  class="checkbox mt-1 mr-3"
                />
                <div>
                  <span class="text-sm font-medium text-ink">
                    Are you a Politically Exposed Person (PEP)?
                  </span>
                  <p class="text-xs text-ink-tertiary mt-1">
                    A PEP is someone who is or has been entrusted with a prominent public function (e.g., heads of state, senior politicians, senior government officials, judicial or military officials, senior executives of state-owned corporations).
                  </p>
                </div>
              </label>
            </div>

            <div class="rounded-xl border border-line p-4">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={formData.immediate_family_exposed}
                  class="checkbox mt-1 mr-3"
                />
                <div>
                  <span class="text-sm font-medium text-ink">
                    Is an immediate family member a Politically Exposed Person?
                  </span>
                  <p class="text-xs text-ink-tertiary mt-1">
                    Immediate family members include spouse, parent, sibling, child, or in-law.
                  </p>
                </div>
              </label>
            </div>

            {#if needsAffiliateDetails}
              <div class="banner banner-warning">
                <p class="font-semibold mb-1">
                  <i class="fas fa-exclamation-triangle mr-2"></i>
                  Additional information required
                </p>
                <p class="text-xs mb-4">
                  Since you indicated an affiliation, please provide the following details. An account approval letter from your compliance department may be required.
                </p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="label" for="onb-affiliate-name">Company name</label>
                    <input
                      id="onb-affiliate-name"
                      type="text"
                      bind:value={formData.affiliated_company_name}
                      placeholder="Company name"
                      class="input bg-surface focus:bg-surface"
                    />
                  </div>
                  <div>
                    <label class="label" for="onb-affiliate-address">Company address</label>
                    <input
                      id="onb-affiliate-address"
                      type="text"
                      bind:value={formData.affiliated_company_address}
                      placeholder="Company address"
                      class="input bg-surface focus:bg-surface"
                    />
                  </div>
                  <div>
                    <label class="label" for="onb-affiliate-ticker">Ticker symbol (if public)</label>
                    <input
                      id="onb-affiliate-ticker"
                      type="text"
                      bind:value={formData.affiliated_company_ticker}
                      placeholder="AAPL"
                      class="input bg-surface focus:bg-surface uppercase"
                    />
                  </div>
                </div>
              </div>
            {/if}
          </div>

        <!-- Step 5: Review & Agreements -->
        {:else if step === 5}
          <h3 class="text-lg font-semibold text-ink mb-1">Review &amp; agreements</h3>
          <p class="text-sm text-ink-secondary mb-6">
            Confirm your details and accept the required agreements to submit your application.
          </p>

          <!-- Summary Card -->
          <div class="rounded-xl border border-line bg-canvas p-5 mb-6">
            <p class="text-sm font-semibold text-ink mb-4">Application summary</p>
            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <dt class="text-xs text-ink-tertiary">Name</dt>
                <dd class="text-sm text-ink">{formData.first_name} {formData.middle_name || ''} {formData.last_name}</dd>
              </div>
              <div>
                <dt class="text-xs text-ink-tertiary">Date of birth</dt>
                <dd class="text-sm text-ink tnum">{formData.date_of_birth}</dd>
              </div>
              <div>
                <dt class="text-xs text-ink-tertiary">Phone</dt>
                <dd class="text-sm text-ink tnum">+1 {formData.phone_number}</dd>
              </div>
              <div>
                <dt class="text-xs text-ink-tertiary">SSN</dt>
                <dd class="text-sm text-ink tnum">***-**-{formData.tax_id.slice(-4)}</dd>
              </div>
              <div class="sm:col-span-2">
                <dt class="text-xs text-ink-tertiary">Address</dt>
                <dd class="text-sm text-ink">{formData.street_address}{formData.unit_apt ? ', ' + formData.unit_apt : ''}, {formData.city}, {formData.state} {formData.postal_code}</dd>
              </div>
              <div>
                <dt class="text-xs text-ink-tertiary">Employment</dt>
                <dd class="text-sm text-ink">{employmentStatuses.find(s => s.value === formData.employment_status)?.label}</dd>
              </div>
              <div>
                <dt class="text-xs text-ink-tertiary">Funding source</dt>
                <dd class="text-sm text-ink">{fundingSources.find(s => s.value === formData.funding_source)?.label}</dd>
              </div>
            </dl>
          </div>

          <!-- Required Disclosures -->
          <div class="mb-6">
            <p class="text-sm font-semibold text-ink mb-1">Required disclosures</p>
            <p class="text-xs text-ink-tertiary mb-3">
              Please review the following important documents before proceeding:
            </p>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <a href={disclosures.useAndRisk} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> Use &amp; Risk
              </a>
              <a href={disclosures.termsAndConditions} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> Terms &amp; Conditions
              </a>
              <a href={disclosures.privacyNotice} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> Privacy Notice
              </a>
              <a href={disclosures.pfof} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> PFOF Disclosure
              </a>
              <a href={disclosures.marginDisclosure} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> Margin Disclosure
              </a>
              <a href={disclosures.extendedHours} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> Extended Hours Risk
              </a>
              <a href={disclosures.bcp} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> BCP Summary
              </a>
              <a href={disclosures.formCRS} target="_blank" rel="noopener" class="flex items-center gap-1.5 rounded-xl border border-line px-3 py-2 text-xs text-ink-secondary hover:bg-surface-variant hover:text-ink transition-colors">
                <i class="fas fa-file-pdf text-ink-tertiary"></i> Form CRS
              </a>
            </div>
          </div>

          <!-- Agreements -->
          <div class="space-y-4">
            <!-- Customer Agreement -->
            <div class="rounded-xl border border-line p-4 {fieldErrors.customer_agreement ? 'input-error' : ''}">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={formData.customer_agreement_accepted}
                  class="checkbox mt-1 mr-3"
                />
                <div>
                  <span class="text-sm text-ink">
                    I have read, understood, and agree to be bound by Alpaca Securities LLC and Moira Finance account terms, and all other terms, disclosures and disclaimers applicable to me, as referenced in the
                    <a href={disclosures.customerAgreement} target="_blank" rel="noopener" class="text-moira hover:text-moira-dark underline">
                      Alpaca Customer Agreement
                    </a>. I also acknowledge that the Alpaca Customer Agreement contains a pre-dispute arbitration clause in Section 43.
                  </span>
                </div>
              </label>
              {#if fieldErrors.customer_agreement}
                <p class="help-error ml-7">{fieldErrors.customer_agreement}</p>
              {/if}
            </div>

            <!-- Electronic Signature -->
            <div class="rounded-xl border border-line p-4 {fieldErrors.electronic_signature ? 'input-error' : ''}">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  bind:checked={formData.electronic_signature_accepted}
                  class="checkbox mt-1 mr-3"
                />
                <div>
                  <span class="text-sm text-ink">
                    I understand I am signing this agreement electronically, and that my electronic signature will have the same effect as physically signing and returning the Application Agreement.
                  </span>
                </div>
              </label>
              {#if fieldErrors.electronic_signature}
                <p class="help-error ml-7">{fieldErrors.electronic_signature}</p>
              {/if}
            </div>

            <!-- Investing Disclaimer -->
            <div class="banner banner-warning">
              <p class="text-xs">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <strong>Investment risk:</strong> Investing involves risk and you may lose money. Past performance does not guarantee future results. Securities products are provided by Alpaca Securities LLC, member FINRA/SIPC.
              </p>
            </div>
          </div>
        {/if}

        <!-- Navigation Buttons (only for steps 1+, step 0 has its own button) -->
        {#if step > 0}
          <div class="flex justify-between mt-8">
            {#if step > 1}
              <button
                on:click={prevStep}
                class="btn btn-outline"
              >
                <i class="fas fa-arrow-left text-ink-tertiary"></i> Back
              </button>
            {:else}
              <button
                on:click={prevStep}
                class="btn btn-outline"
              >
                <i class="fas fa-arrow-left text-ink-tertiary"></i> Back
              </button>
            {/if}

            {#if step < totalSteps - 1}
              <button
                on:click={nextStep}
                class="btn btn-primary"
              >
                Continue <i class="fas fa-arrow-right"></i>
              </button>
            {:else}
              <button
                on:click={handleSubmit}
                disabled={submitting}
                class="btn btn-primary"
              >
                {#if submitting}
                  <i class="fas fa-spinner fa-spin"></i>
                  Creating account...
                {:else}
                  <i class="fas fa-check"></i>
                  Submit application
                {/if}
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </div>

    <!-- Help Section -->
    <div class="text-center text-xs text-ink-tertiary mb-6">
      <p>
        Need help? Contact support at <a href="mailto:support@moirafinance.com" class="text-moira hover:text-moira-dark">support@moirafinance.com</a>
      </p>
      <p class="mt-2">
        <a href={disclosures.disclosuresPage} target="_blank" rel="noopener" class="text-moira hover:text-moira-dark">
          View all Alpaca disclosures
        </a>
      </p>
    </div>
  {/if}
</div>
